// frontend/src/pages/AdminPage.js
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { formatCurrency } from '../services/utils';
import { Modal, Button, Form, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';

function AdminPage() {
  const [universidades, setUniversidades] = useState([]);
  const [newUniNombre, setNewUniNombre] = useState('');
  const [newUniSiglas, setNewUniSiglas] = useState('');
  const [selectedUniForFac, setSelectedUniForFac] = useState('');
  const [newFacNombre, setNewFacNombre] = useState('');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);
  const [loadingUnis, setLoadingUnis] = useState(true);
  const [error, setError] = useState('');

  // Reportes
  const [reportes, setReportes] = useState([]);
  const [reportesLoading, setReportesLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('PENDIENTE');
  const [resolviendoId, setResolviendoId] = useState(null);

  // Modal edicion
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editType, setEditType] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  const fetchUniversidades = useCallback(async (showLoading = true) => {
    setError('');
    if (showLoading) setLoadingUnis(true);
    try {
      const r = await api.get('/general/universidades');
      setUniversidades(r.data);
    } catch (err) { setError('Error al cargar universidades.'); }
    finally { if (showLoading) setLoadingUnis(false); }
  }, []);

  const fetchUsers = useCallback(async (showLoading = true) => {
    if (showLoading) setUsersLoading(true);
    try { const r = await api.get('/admin/users'); setUsers(r.data); }
    catch (err) { setError('Error al cargar usuarios.'); }
    finally { if (showLoading) setUsersLoading(false); }
  }, []);

  const fetchItems = useCallback(async (showLoading = true) => {
    if (showLoading) setItemsLoading(true);
    try { const r = await api.get('/admin/items'); setItems(r.data); }
    catch (err) { setError('Error al cargar items.'); }
    finally { if (showLoading) setItemsLoading(false); }
  }, []);

  const fetchAuditoria = useCallback(async (showLoading = true) => {
    if (showLoading) setAuditLoading(true);
    try { const r = await api.get('/admin/auditoria?limit=50'); setAuditLogs(r.data); }
    catch (err) { console.error('Error auditoria:', err); }
    finally { if (showLoading) setAuditLoading(false); }
  }, []);

  const fetchReportes = useCallback(async (estado = filtroEstado) => {
    setReportesLoading(true);
    try {
      const r = await api.get('/reportes/', { params: { estado, limit: 50 } });
      setReportes(r.data);
    } catch (err) { console.error('Error reportes:', err); }
    finally { setReportesLoading(false); }
  }, [filtroEstado]);

  useEffect(() => {
    fetchUniversidades();
    fetchUsers();
    fetchItems();
    fetchAuditoria();
    fetchReportes();
  }, [fetchUniversidades, fetchUsers, fetchItems, fetchAuditoria, fetchReportes]);

  const handleFiltroEstado = (estado) => {
    setFiltroEstado(estado);
    fetchReportes(estado);
  };

  const handleResolver = async (reporteId, nuevoEstado) => {
    const etiqueta = nuevoEstado === 'REVISADO' ? 'tomar accion' : 'descartar';
    if (!window.confirm(`Confirmas ${etiqueta} este reporte?`)) return;
    setResolviendoId(reporteId);
    try {
      await api.patch(`/reportes/${reporteId}/resolver?nuevo_estado=${nuevoEstado}`);
      fetchReportes(filtroEstado);
    } catch (e) {
      alert('Error al resolver reporte.');
    } finally {
      setResolviendoId(null);
    }
  };

  const handleCreateUniversidad = async (e) => {
    e.preventDefault(); setError('');
    try {
      await api.post('/general/universidades', { nombre: newUniNombre, siglas: newUniSiglas });
      setNewUniNombre(''); setNewUniSiglas('');
      fetchUniversidades(false);
    } catch (err) { setError(err.response?.data?.detail || 'Error.'); }
  };

  const handleDeleteUniversidad = async (uniId) => {
    if (window.confirm('Seguro?')) {
      try { await api.delete(`/general/universidades/${uniId}`); fetchUniversidades(false); }
      catch (err) { setError(err.response?.data?.detail || 'Error.'); }
    }
  };

  const handleCreateFacultad = async (e) => {
    e.preventDefault();
    try {
      await api.post('/general/facultades', { nombre: newFacNombre, universidad_id: parseInt(selectedUniForFac) });
      setNewFacNombre(''); fetchUniversidades(false);
    } catch (err) { setError(err.response?.data?.detail || 'Error.'); }
  };

  const handleDeleteFacultad = async (facId) => {
    if (window.confirm('Seguro?'))
      try { await api.delete(`/general/facultades/${facId}`); fetchUniversidades(false); }
      catch (e) { setError('Error'); }
  };

  const handleDeleteUser = async (uid) => {
    if (window.confirm('Seguro?'))
      try { await api.delete(`/admin/users/${uid}`); setUsers(u => u.filter(x => x.id !== uid)); }
      catch (e) { setError('Error'); }
  };

  const handleDeleteItem = async (iid) => {
    if (window.confirm('Seguro?'))
      try { await api.delete(`/admin/items/${iid}`); setItems(i => i.filter(x => x.id !== iid)); }
      catch (e) { setError('Error'); }
  };

  const handleShowEditModal = (item, type) => {
    setEditType(type);
    setEditingItem({ id: item.id, nombre: item.nombre, siglas: item.siglas || '', universidad_id: item.universidad_id });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault(); setEditLoading(true);
    try {
      const endpoint = editType === 'universidad'
        ? `/admin/universidades/${editingItem.id}`
        : `/admin/facultades/${editingItem.id}`;
      await api.put(endpoint, editingItem);
      fetchUniversidades(false);
      setShowEditModal(false);
    } catch (e) { setError('Error al editar'); }
    finally { setEditLoading(false); }
  };

  const estadoBadge = (estado) => {
    const map = { PENDIENTE: 'warning', REVISADO: 'success', DESCARTADO: 'secondary' };
    return <Badge bg={map[estado] || 'light'}>{estado}</Badge>;
  };

  return (
    <div>
      <h1>Panel de Administracion</h1>
      {error && !showEditModal && <div className="alert alert-danger">{error}</div>}

      {/* SECCION REPORTES */}
      <div className="card mt-4 border-danger">
        <div className="card-header bg-danger text-white fw-bold d-flex justify-content-between align-items-center">
          <span>
            <i className="bi bi-flag me-2"></i>
            Reportes de usuarios
            {reportes.filter(r => r.estado === 'PENDIENTE').length > 0 && (
              <span className="badge bg-white text-danger ms-2">
                {reportes.filter(r => r.estado === 'PENDIENTE').length} pendientes
              </span>
            )}
          </span>
          <div className="d-flex gap-2">
            {['PENDIENTE', 'REVISADO', 'DESCARTADO'].map(e => (
              <button
                key={e}
                className={`btn btn-sm ${filtroEstado === e ? 'btn-white' : 'btn-outline-light'}`}
                style={filtroEstado === e ? { background: 'white', color: '#dc3545' } : {}}
                onClick={() => handleFiltroEstado(e)}
              >
                {e}
              </button>
            ))}
          </div>
        </div>
        <div className="card-body p-0">
          {reportesLoading ? (
            <div className="p-3 text-muted">Cargando reportes...</div>
          ) : reportes.length === 0 ? (
            <div className="p-4 text-center text-muted">
              <i className="bi bi-check-circle fs-2 d-block mb-2"></i>
              No hay reportes {filtroEstado.toLowerCase()}s.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.9rem' }}>
                <thead className="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Reportado por</th>
                    <th>Objetivo</th>
                    <th>Motivo</th>
                    <th>Descripcion</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {reportes.map(r => (
                    <tr key={r.id}>
                      <td><small>{new Date(r.fecha).toLocaleDateString()}</small></td>
                      <td>
                        {r.reportado_por ? (
                          <Link to={`/perfil/${r.reportado_por.id}`} className="text-decoration-none">
                            {r.reportado_por.username}
                          </Link>
                        ) : '-'}
                      </td>
                      <td>
                        {r.item ? (
                          <Link to={`/items/${r.item.id}`} className="text-decoration-none">
                            <i className="bi bi-box-seam me-1 text-muted"></i>
                            {r.item.titulo}
                          </Link>
                        ) : r.usuario_reportado ? (
                          <Link to={`/perfil/${r.usuario_reportado.id}`} className="text-decoration-none">
                            <i className="bi bi-person me-1 text-muted"></i>
                            {r.usuario_reportado.username}
                          </Link>
                        ) : '-'}
                      </td>
                      <td><span className="badge bg-secondary">{r.motivo}</span></td>
                      <td className="text-truncate" style={{ maxWidth: '180px' }} title={r.descripcion}>
                        {r.descripcion || <span className="text-muted">-</span>}
                      </td>
                      <td>{estadoBadge(r.estado)}</td>
                      <td>
                        {r.estado === 'PENDIENTE' && (
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-xs btn-success"
                              style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                              disabled={resolviendoId === r.id}
                              onClick={() => handleResolver(r.id, 'REVISADO')}
                              title="Marcar como revisado (accion tomada)"
                            >
                              <i className="bi bi-check2"></i> Revisar
                            </button>
                            <button
                              className="btn btn-xs btn-outline-secondary"
                              style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                              disabled={resolviendoId === r.id}
                              onClick={() => handleResolver(r.id, 'DESCARTADO')}
                              title="Descartar reporte"
                            >
                              <i className="bi bi-x"></i> Descartar
                            </button>
                          </div>
                        )}
                        {r.estado !== 'PENDIENTE' && r.revisado_por && (
                          <small className="text-muted">por {r.revisado_por.username}</small>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* SECCION AUDITORIA */}
      <div className="card mt-4 border-info">
        <div className="card-header bg-info text-white fw-bold d-flex justify-content-between align-items-center">
          <span>Registro de Auditoria (ultimos 50)</span>
          <button className="btn btn-sm btn-light" onClick={() => fetchAuditoria()}>Refrescar</button>
        </div>
        <div className="card-body p-0">
          {auditLoading ? <div className="p-3">Cargando logs...</div> : (
            <div className="table-responsive" style={{ maxHeight: '350px', overflowY: 'auto' }}>
              <table className="table table-sm table-striped table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                <thead className="table-light sticky-top">
                  <tr><th>Fecha</th><th>Usuario</th><th>Accion</th><th>Tabla</th><th>ID</th><th>IP</th></tr>
                </thead>
                <tbody>
                  {auditLogs.map(log => (
                    <tr key={log.id}>
                      <td>{new Date(log.fecha).toLocaleString()}</td>
                      <td>{log.usuario ? <span className="badge bg-secondary">{log.usuario.username}</span> : <span className="text-muted fst-italic">Sistema</span>}</td>
                      <td className="fw-bold">{log.accion}</td>
                      <td>{log.tabla_afectada || '-'}</td>
                      <td>{log.registro_id || '-'}</td>
                      <td><small>{log.ip_address}</small></td>
                    </tr>
                  ))}
                  {auditLogs.length === 0 && <tr><td colSpan="6" className="text-center p-3">Sin registros.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* SECCION UNIVERSIDADES */}
      <div className="card mt-4">
        <div className="card-header">Gestionar Universidades y Facultades</div>
        <div className="card-body">
          <form onSubmit={handleCreateUniversidad} className="row g-3 mb-4 border-bottom pb-3">
            <div className="col-md-6"><input type="text" className="form-control" placeholder="Nombre Universidad" value={newUniNombre} onChange={e => setNewUniNombre(e.target.value)} required /></div>
            <div className="col-md-4"><input type="text" className="form-control" placeholder="Siglas" value={newUniSiglas} onChange={e => setNewUniSiglas(e.target.value)} /></div>
            <div className="col-md-2"><button type="submit" className="btn btn-primary w-100">Anadir</button></div>
          </form>
          <form onSubmit={handleCreateFacultad} className="row g-3 mb-4 border-bottom pb-3 pt-3">
            <div className="col-md-5">
              <select className="form-select" value={selectedUniForFac} onChange={e => setSelectedUniForFac(e.target.value)} required>
                <option value="">Selecciona Universidad</option>
                {universidades.map(uni => <option key={uni.id} value={uni.id}>{uni.nombre}</option>)}
              </select>
            </div>
            <div className="col-md-5"><input type="text" className="form-control" placeholder="Nueva Facultad" value={newFacNombre} onChange={e => setNewFacNombre(e.target.value)} required /></div>
            <div className="col-md-2"><button type="submit" className="btn btn-primary w-100">Anadir</button></div>
          </form>
          {loadingUnis ? <p>Cargando...</p> : universidades.map(uni => (
            <div key={uni.id} className="mb-3 p-2 border rounded">
              <div className="d-flex justify-content-between align-items-center">
                <strong>{uni.nombre} ({uni.siglas || 'N/A'})</strong>
                <div>
                  <button className="btn btn-sm btn-outline-warning me-2" onClick={() => handleShowEditModal(uni, 'universidad')}>Editar</button>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteUniversidad(uni.id)}>Eliminar</button>
                </div>
              </div>
              {uni.facultades && (
                <ul className="list-group list-group-flush mt-2">
                  {uni.facultades.map(fac => (
                    <li key={fac.id} className="list-group-item d-flex justify-content-between align-items-center ps-4">
                      - {fac.nombre}
                      <div>
                        <button className="btn btn-sm btn-outline-warning me-2" onClick={() => handleShowEditModal(fac, 'facultad')}>Editar</button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteFacultad(fac.id)}>Eliminar</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* SECCION USUARIOS */}
      <div className="card mt-4">
        <div className="card-header">Gestionar Usuarios</div>
        <div className="card-body">
          {usersLoading ? <p>Cargando...</p> : (
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead><tr><th>ID</th><th>Username</th><th>Email</th><th>Verificado</th><th>Acciones</th></tr></thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td><Link to={`/perfil/${user.id}`}>{user.username}</Link></td>
                      <td>{user.email}</td>
                      <td>{user.email_verificado ? 'Si' : 'No'}</td>
                      <td><button className="btn btn-sm btn-danger" onClick={() => handleDeleteUser(user.id)} disabled={user.username === 'admin'}>Eliminar</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* SECCION ITEMS */}
      <div className="card mt-4 mb-5">
        <div className="card-header">Gestionar Publicaciones</div>
        <div className="card-body">
          {itemsLoading ? <p>Cargando...</p> : (
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead><tr><th>ID</th><th>Titulo</th><th>Precio</th><th>Vendedor</th><th>Acciones</th></tr></thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td><Link to={`/items/${item.id}`}>{item.titulo}</Link></td>
                      <td>{formatCurrency(item.precio)}</td>
                      <td>{item.propietario?.username || 'N/A'}</td>
                      <td><button className="btn btn-sm btn-danger" onClick={() => handleDeleteItem(item.id)}>Eliminar</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal edicion */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Header closeButton><Modal.Title>Editar</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control name="nombre" value={editingItem?.nombre || ''} onChange={e => setEditingItem({ ...editingItem, nombre: e.target.value })} required />
            </Form.Group>
            {editType === 'universidad' && (
              <Form.Group>
                <Form.Label>Siglas</Form.Label>
                <Form.Control name="siglas" value={editingItem?.siglas || ''} onChange={e => setEditingItem({ ...editingItem, siglas: e.target.value })} />
              </Form.Group>
            )}
            {editType === 'facultad' && (
              <Form.Group>
                <Form.Label>Universidad</Form.Label>
                <Form.Select value={editingItem?.universidad_id || ''} onChange={e => setEditingItem({ ...editingItem, universidad_id: e.target.value })}>
                  <option value="">Seleccionar...</option>
                  {universidades.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
                </Form.Select>
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancelar</Button>
            <Button variant="primary" type="submit" disabled={editLoading}>Guardar</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default AdminPage;
